import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';

interface IImageViewerBackgroundProps {
  uri: string;
  opacity?: number;
}

/**
 * Full-screen blurred backdrop for the image viewer.
 *
 * On Android, `expo-image` `blurRadius` is implemented with a Glide blur that
 * uses RenderScript. That path has produced native fatal crashes in production
 * (e.g. RenderScript / `ScriptIntrinsic` tombstones). We avoid `blurRadius` on
 * Android here and use a solid black background instead. iOS and web keep the
 * blurred image plus dimming overlay.
 *
 * On web, `blurRadius` becomes a CSS `filter` on the `<img>`, which browsers without
 * GPU rasterization blur on the CPU tile by tile (~1.6s for a full-screen 80px blur,
 * stalling the open animation). Putting the filter on a `will-change: filter` wrapper
 * lets the compositor apply it on the GPU instead.
 */
export const ImageViewerBackground = React.memo<IImageViewerBackgroundProps>(({
  uri,
  opacity = 0.35,
}) => {
  if (Platform.OS === 'android') {
    return <View style={[StyleSheet.absoluteFill, { backgroundColor: 'black' }]} pointerEvents="none" />;
  }

  const image = (
    <Image
      source={{ uri }}
      style={StyleSheet.absoluteFill}
      contentFit="cover"
      transition={{ duration: 200, effect: 'cross-dissolve' }}
      blurRadius={Platform.OS === 'web' ? undefined : 80}
    />
  );

  return (
    <>
      {Platform.OS === 'web' ? (
        <View style={[StyleSheet.absoluteFill, { overflow: 'hidden' }]}>
          <View style={[StyleSheet.absoluteFill, { filter: 'blur(80px)', willChange: 'filter' } as any]}>
            {image}
          </View>
        </View>
      ) : (
        image
      )}
      <View
        style={[StyleSheet.absoluteFill, { backgroundColor: `rgba(0, 0, 0, ${opacity})` }]}
        pointerEvents="none"
      />
    </>
  );
});

ImageViewerBackground.displayName = 'ImageViewerBackground';
